import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSentinelBusy } from "../lib/sentinel-busy";
import { Shield, Radio, Eye, Loader2 } from "lucide-react";
import { useState } from "react";
import TrialBanner from "./TrialBanner";
import DemoBanner from "./DemoBanner";

export default function TopNav({ landing = false }) {
  const { user, logout, demoLogin } = useAuth();
  const { busyCount } = useSentinelBusy();
  const nav = useNavigate();
  const isAuthed = user && user.id;
  const sentinelBusy = busyCount > 0;
  const [demoLoading, setDemoLoading] = useState(false);

  const startDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      const u = await demoLogin();
      nav(u?.is_demo ? "/assistant?demo=1" : "/dashboard");
    } catch (e) {
      console.error("demo-login failed", e);
      setDemoLoading(false);
    }
  };

  return (
    <header className={landing ? "crystal-header sticky top-0 z-50" : "border-b border-[var(--border)] bg-[var(--bg)]"} data-testid="top-nav">
      {!landing && isAuthed && <DemoBanner />}
      {!landing && isAuthed && <TrialBanner />}
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <Shield size={22} strokeWidth={1.5} color="#00E5FF" />
          <div className="font-display text-lg tracking-widest uppercase">
            <span className="text-white">Def</span><span className="text-[var(--cyan)]">Dossier</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">
          {landing ? (
            <>
              <a href="#mission" className="hover:text-white" data-testid="nav-mission">Mission</a>
              <a href="#tracks" className="hover:text-white" data-testid="nav-tracks">Tracks</a>
              <a href="#model" className="hover:text-white" data-testid="nav-model">Model</a>
              <a href="#metrics" className="hover:text-white" data-testid="nav-metrics">Measures</a>
            </>
          ) : (
            <>
              <Link to="/dashboard" className="hover:text-white" data-testid="nav-dashboard">Ops</Link>
              <Link to="/courses" className="hover:text-white" data-testid="nav-courses">Courses</Link>
              <Link to="/exercises" className="hover:text-white" data-testid="nav-exercises">Exercises</Link>
              <Link to="/practical" className="hover:text-white" data-testid="nav-practical">Range</Link>
              <Link to="/scenarios" className="hover:text-white" data-testid="nav-scenarios">Library</Link>
              <Link to="/coverage" className="hover:text-white" data-testid="nav-coverage">Coverage</Link>
              <Link to="/badges" className="hover:text-white" data-testid="nav-badges">Badges</Link>
              <Link to="/metrics" className="hover:text-white" data-testid="nav-metrics-link">Readiness</Link>
              {(user?.role === "leader" || user?.role === "org_admin" || user?.role === "admin") && (
                <Link to="/squad" className="hover:text-white text-[var(--amber)]" data-testid="nav-squad">Squad</Link>
              )}
              {(user?.role === "org_admin" || user?.role === "admin") && (
                <Link to="/org" className="hover:text-white text-[var(--cyan)]" data-testid="nav-org">HQ</Link>
              )}
              {(user?.role === "org_admin" || user?.role === "admin") && (
                <Link to="/security" className="hover:text-white text-[var(--cyan)]" data-testid="nav-security">Trust</Link>
              )}
              <Link to="/assistant" className="hover:text-white" data-testid="nav-assistant">Sentinel</Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {isAuthed && sentinelBusy && (
            <span
              className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-[var(--cyan)] border border-[var(--cyan)] px-2 py-0.5"
              data-testid="sentinel-busy-indicator"
              title="Sentinel is processing a streaming task"
            >
              <Radio size={11} strokeWidth={1.5} className="animate-pulse" />
              <span className="blink">Sentinel thinking</span>
            </span>
          )}
          {isAuthed ? (
            <>
              <span className="hidden sm:inline font-mono text-xs text-[var(--text-2)] uppercase tracking-widest" data-testid="user-badge">
                {user.name} / {user.track}
              </span>
              <button className="btn-ghost" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="login-link">Login</Link>
              <button onClick={startDemo} disabled={demoLoading} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="enlist-btn">
                {demoLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                {demoLoading ? "Loading…" : "Try Demo"}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
